export interface AreaEntry {
  name: string;
  slug: string;
}

export interface CityData {
  name: string;
  nameEn: string;
  kommuner: AreaEntry[];
  stadsdelar: AreaEntry[];
}

const sort = (arr: AreaEntry[]) =>
  [...arr].sort((a, b) => a.name.localeCompare(b.name, "sv"));

export const cities: Record<"stockholm" | "göteborg", CityData> = {
  stockholm: {
    name: "Stockholm",
    nameEn: "Stockholm",
    kommuner: sort([
      { name: "Botkyrka", slug: "botkyrka" },
      { name: "Danderyd", slug: "danderyd" },
      { name: "Ekerö", slug: "ekero" },
      { name: "Haninge", slug: "haninge" },
      { name: "Huddinge", slug: "huddinge" },
      { name: "Järfälla", slug: "jarfalla" },
      { name: "Lidingö", slug: "lidingo" },
      { name: "Nacka", slug: "nacka" },
      { name: "Norrtälje", slug: "norrtalje" },
      { name: "Nykvarn", slug: "nykvarn" },
      { name: "Nynäshamn", slug: "nynashamn" },
      { name: "Salem", slug: "salem" },
      { name: "Sigtuna", slug: "sigtuna" },
      { name: "Sollentuna", slug: "sollentuna" },
      { name: "Solna", slug: "solna" },
      { name: "Stockholm", slug: "stockholm" },
      { name: "Sundbyberg", slug: "sundbyberg" },
      { name: "Södertälje", slug: "sodertalje" },
      { name: "Tyresö", slug: "tyreso" },
      { name: "Täby", slug: "taby" },
      { name: "Upplands Väsby", slug: "upplands-vasby" },
      { name: "Upplands-Bro", slug: "upplands-bro" },
      { name: "Vallentuna", slug: "vallentuna" },
      { name: "Vaxholm", slug: "vaxholm" },
      { name: "Värmdö", slug: "varmdo" },
      { name: "Österåker", slug: "osteraker" },
    ]),
    stadsdelar: sort([
      { name: "Aspudden", slug: "aspudden" },
      { name: "Bagarmossen", slug: "bagarmossen" },
      { name: "Bandhagen", slug: "bandhagen" },
      { name: "Bredäng", slug: "bredang" },
      { name: "Bromma", slug: "bromma" },
      { name: "Enskede", slug: "enskede" },
      { name: "Farsta", slug: "farsta" },
      { name: "Fruängen", slug: "fruangen" },
      { name: "Gröndal", slug: "grondal" },
      { name: "Gubbängen", slug: "gubbangen" },
      { name: "Hammarbyhöjden", slug: "hammarbyhojden" },
      { name: "Hägersten", slug: "hagersten" },
      { name: "Hässelby", slug: "hasselby" },
      { name: "Hökarängen", slug: "hokarangen" },
      { name: "Kista", slug: "kista" },
      { name: "Kungsholmen", slug: "kungsholmen" },
      { name: "Liljeholmen", slug: "liljeholmen" },
      { name: "Midsommarkransen", slug: "midsommarkransen" },
      { name: "Norrmalm", slug: "norrmalm" },
      { name: "Rågsved", slug: "ragsved" },
      { name: "Skarpnäck", slug: "skarpnack" },
      { name: "Skärholmen", slug: "skarholmen" },
      { name: "Solberga", slug: "solberga" },
      { name: "Spånga", slug: "spanga" },
      { name: "Södermalm", slug: "sodermalm" },
      { name: "Vasastan", slug: "vasastan" },
      { name: "Vällingby", slug: "vallingby" },
      { name: "Älvsjö", slug: "alvsjo" },
      { name: "Årsta", slug: "arsta" },
      { name: "Östermalm", slug: "ostermalm" },
    ]),
  },
  göteborg: {
    name: "Göteborgsregionen",
    nameEn: "Gothenburg Region",
    kommuner: sort([
      { name: "Ale", slug: "ale" },
      { name: "Alingsås", slug: "alingsas" },
      { name: "Göteborg", slug: "goteborg" },
      { name: "Härryda", slug: "harryda" },
      { name: "Kungälv", slug: "kungalv" },
      { name: "Kungsbacka", slug: "kungsbacka" },
      { name: "Lerum", slug: "lerum" },
      { name: "Lilla Edet", slug: "lilla-edet" },
      { name: "Mölndal", slug: "molndal" },
      { name: "Partille", slug: "partille" },
      { name: "Stenungsund", slug: "stenungsund" },
      { name: "Tjörn", slug: "tjorn" },
      { name: "Öckerö", slug: "ockero" },
    ]),
    stadsdelar: sort([
      { name: "Angered", slug: "angered" },
      { name: "Askim", slug: "askim" },
      { name: "Backa", slug: "backa" },
      { name: "Bagaregården", slug: "bagaregarden" },
      { name: "Biskopsgården", slug: "biskopsgarden" },
      { name: "Eriksberg", slug: "eriksberg" },
      { name: "Frölunda", slug: "frolunda" },
      { name: "Gamlestaden", slug: "gamlestaden" },
      { name: "Guldheden", slug: "guldheden" },
      { name: "Haga", slug: "haga" },
      { name: "Hisingen", slug: "hisingen" },
      { name: "Härlanda", slug: "harlanda" },
      { name: "Högsbo", slug: "hogsbo" },
      { name: "Johanneberg", slug: "johanneberg" },
      { name: "Kortedala", slug: "kortedala" },
      { name: "Kålltorp", slug: "kalltorp" },
      { name: "Linnéstaden", slug: "linnestaden" },
      { name: "Lundby", slug: "lundby" },
      { name: "Majorna", slug: "majorna" },
      { name: "Masthugget", slug: "masthugget" },
      { name: "Torslanda", slug: "torslanda" },
      { name: "Tynnered", slug: "tynnered" },
      { name: "Örgryte", slug: "orgryte" },
    ]),
  },
};

/** Get a comma-separated municipality list for a city (useful in FAQ text). */
export function getCityMunicipalityList(cityId: "stockholm" | "göteborg"): string {
  return cities[cityId].kommuner.map((k) => k.name).join(", ");
}
